import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { Invitation, UserRole, InvitationType } from '../../types/organization.types';
import { employeesService } from './employees.service';

/**
 * Generate a secure random token for invitation URLs
 */
function generateInvitationToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Get the base URL for invitation links
 */
function getInvitationBaseUrl(): string {
  // In production, this should come from environment variables
  return window.location.origin;
}

/**
 * Firebase Invitations Service
 * Handles sending, accepting, and managing organization invitations
 */
export const invitationsService = {
  /**
   * Create a staff invitation
   * For inviting team members (admins, managers, viewers)
   */
  async createStaffInvitation(
    organizationId: string,
    email: string,
    role: Exclude<UserRole, 'owner' | 'employee'>,
    invitedBy: string,
    options: {
      organizationName: string;
      inviterName?: string;
    }
  ): Promise<{ invitationId: string; token: string; invitationLink: string }> {
    try {
      // Check if there's already a pending invitation for this email
      const existingInvitation = await this.getPendingInvitationByEmail(organizationId, email);
      if (existingInvitation) {
        throw new Error('An invitation for this email already exists');
      }

      const token = generateInvitationToken();
      const invitationRef = doc(collection(db, 'organizations', organizationId, 'invitations'));
      const invitationId = invitationRef.id;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiration

      await setDoc(invitationRef, {
        email: email.toLowerCase(),
        role,
        invitedBy,
        status: 'pending',
        invitationType: 'staff' as InvitationType,
        token,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      // Create token lookup document for public access (includes data for displaying invite page)
      await this.createInvitationTokenDocument(token, organizationId, invitationId, {
        email: email.toLowerCase(),
        role,
        invitationType: 'staff',
        organizationName: options.organizationName,
        expiresAt,
      });

      const invitationLink = `${getInvitationBaseUrl()}/invite/${token}`;

      return { invitationId, token, invitationLink };
    } catch (error) {
      console.error('Create staff invitation error:', error);
      throw error;
    }
  },

  /**
   * Create an employee invitation
   * For inviting employees (shift workers) to create accounts
   */
  async createEmployeeInvitation(
    organizationId: string,
    employeeId: string,
    email: string,
    invitedBy: string,
    options: {
      employeeName: string;
      organizationName: string;
    }
  ): Promise<{ invitationId: string; token: string; invitationLink: string }> {
    try {
      // Check if there's already a pending invitation for this employee
      const existingInvitation = await this.getPendingInvitationByEmployeeId(organizationId, employeeId);
      if (existingInvitation) {
        throw new Error('An invitation for this employee already exists. Use "Get New Invitation Link" instead.');
      }

      const token = generateInvitationToken();
      const invitationRef = doc(collection(db, 'organizations', organizationId, 'invitations'));
      const invitationId = invitationRef.id;

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiration

      await setDoc(invitationRef, {
        email: email.toLowerCase(),
        role: 'employee' as UserRole,
        invitedBy,
        status: 'pending',
        invitationType: 'employee' as InvitationType,
        employeeId,
        token,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
      });

      // Create token lookup document for public access (includes data for displaying invite page)
      await this.createInvitationTokenDocument(token, organizationId, invitationId, {
        email: email.toLowerCase(),
        role: 'employee',
        invitationType: 'employee',
        organizationName: options.organizationName,
        employeeName: options.employeeName,
        employeeId,
        expiresAt,
      });

      // Update employee's account status to 'invited'
      await employeesService.updateAccountStatus(organizationId, employeeId, 'invited');

      const invitationLink = `${getInvitationBaseUrl()}/invite/${token}`;

      return { invitationId, token, invitationLink };
    } catch (error) {
      console.error('Create employee invitation error:', error);
      throw error;
    }
  },

  /**
   * Create a token lookup document in the invitationTokens collection
   * This allows public lookup of invitations by token without knowing the organization
   * Stores enough info to display the invitation page without reading protected documents
   */
  async createInvitationTokenDocument(
    token: string,
    organizationId: string,
    invitationId: string,
    publicData: {
      email: string;
      role: UserRole;
      invitationType: InvitationType;
      organizationName: string;
      employeeName?: string;
      employeeId?: string;
      expiresAt: Date;
    }
  ): Promise<void> {
    try {
      await setDoc(doc(db, 'invitationTokens', token), {
        organizationId,
        invitationId,
        // Public data for displaying invitation page without auth
        email: publicData.email,
        role: publicData.role,
        invitationType: publicData.invitationType,
        organizationName: publicData.organizationName,
        employeeName: publicData.employeeName || null,
        employeeId: publicData.employeeId || null,
        expiresAt: Timestamp.fromDate(publicData.expiresAt),
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Create invitation token document error:', error);
      throw error;
    }
  },

  /**
   * Get invitation by token
   * Used for the /invite/:token route
   * Returns public data from token document (works without authentication)
   */
  async getInvitationByToken(token: string): Promise<{
    invitation: Invitation;
    organizationName: string;
  } | null> {
    try {
      // Look up the token document (public access)
      const tokenDoc = await getDoc(doc(db, 'invitationTokens', token));
      if (!tokenDoc.exists()) {
        return null;
      }

      const tokenData = tokenDoc.data() as {
        organizationId: string;
        invitationId: string;
        email: string;
        role: UserRole;
        invitationType: InvitationType;
        organizationName: string;
        employeeName?: string;
        employeeId?: string;
        expiresAt: Timestamp;
      };

      // Check if expired
      const now = new Date();
      const expiresAt = tokenData.expiresAt.toDate();
      if (now > expiresAt) {
        return null; // Token expired
      }

      // Build invitation object from public token data
      // This avoids needing to read the protected invitation document
      const invitation: Invitation = {
        id: tokenData.invitationId,
        organizationId: tokenData.organizationId,
        email: tokenData.email,
        role: tokenData.role,
        invitationType: tokenData.invitationType,
        status: 'pending', // If token exists, invitation is pending
        token: token,
        invitedBy: '', // Not needed for display
        createdAt: Timestamp.now(), // Not critical for display
        expiresAt: tokenData.expiresAt,
        employeeId: tokenData.employeeId, // For employee invitations
      };

      // Add employeeName to invitation if it's an employee invitation
      if (tokenData.employeeName) {
        (invitation as Invitation & { employeeName?: string }).employeeName = tokenData.employeeName;
      }

      return {
        invitation,
        organizationName: tokenData.organizationName,
      };
    } catch (error) {
      console.error('Get invitation by token error:', error);
      throw error;
    }
  },

  /**
   * Get all invitations for an organization
   */
  async getOrganizationInvitations(organizationId: string): Promise<Invitation[]> {
    try {
      const invitationsRef = collection(db, 'organizations', organizationId, 'invitations');
      const querySnapshot = await getDocs(invitationsRef);

      const invitations: Invitation[] = [];
      querySnapshot.forEach((doc) => {
        invitations.push({
          id: doc.id,
          organizationId,
          ...doc.data(),
        } as Invitation);
      });

      return invitations;
    } catch (error) {
      console.error('Get organization invitations error:', error);
      throw error;
    }
  },

  /**
   * Get pending invitation by email
   * Used to check if a user has a pending invitation when they sign up/log in
   */
  async getPendingInvitationByEmail(
    organizationId: string,
    email: string
  ): Promise<Invitation | null> {
    try {
      const invitationsRef = collection(db, 'organizations', organizationId, 'invitations');
      const q = query(
        invitationsRef,
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'pending')
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const docSnap = querySnapshot.docs[0];
      return {
        id: docSnap.id,
        organizationId,
        ...docSnap.data(),
      } as Invitation;
    } catch (error) {
      console.error('Get pending invitation error:', error);
      throw error;
    }
  },

  /**
   * Get pending invitation by employee ID
   */
  async getPendingInvitationByEmployeeId(
    organizationId: string,
    employeeId: string
  ): Promise<Invitation | null> {
    try {
      const invitationsRef = collection(db, 'organizations', organizationId, 'invitations');
      const q = query(
        invitationsRef,
        where('employeeId', '==', employeeId),
        where('status', '==', 'pending')
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const docSnap = querySnapshot.docs[0];
      return {
        id: docSnap.id,
        organizationId,
        ...docSnap.data(),
      } as Invitation;
    } catch (error) {
      console.error('Get pending invitation by employee ID error:', error);
      throw error;
    }
  },

  /**
   * Accept an invitation
   * Updates invitation status
   */
  async acceptInvitation(
    organizationId: string,
    invitationId: string,
    userId: string
  ): Promise<Invitation> {
    try {
      const invitationRef = doc(db, 'organizations', organizationId, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);

      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }

      const invitation = {
        id: invitationDoc.id,
        organizationId,
        ...invitationDoc.data(),
      } as Invitation;

      // Check if invitation is still valid
      if (invitation.status !== 'pending') {
        throw new Error('Invitation is no longer valid');
      }

      const now = new Date();
      const expiresAt = invitation.expiresAt.toDate();
      if (now > expiresAt) {
        // Mark as expired
        await updateDoc(invitationRef, {
          status: 'expired',
        });
        throw new Error('Invitation has expired');
      }

      // Update invitation status
      await updateDoc(invitationRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        acceptedByUserId: userId,
      });

      // Delete the token lookup document
      if (invitation.token) {
        await deleteDoc(doc(db, 'invitationTokens', invitation.token));
      }

      return invitation;
    } catch (error) {
      console.error('Accept invitation error:', error);
      throw error;
    }
  },

  /**
   * Cancel an invitation
   * Only the person who sent it or an admin can cancel
   */
  async cancelInvitation(organizationId: string, invitationId: string): Promise<void> {
    try {
      // Get the invitation to find the token
      const invitationRef = doc(db, 'organizations', organizationId, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);

      if (invitationDoc.exists()) {
        const invitation = invitationDoc.data() as Invitation;

        // Delete the token lookup document if it exists
        if (invitation.token) {
          await deleteDoc(doc(db, 'invitationTokens', invitation.token));
        }
      }

      await updateDoc(invitationRef, {
        status: 'cancelled',
      });
    } catch (error) {
      console.error('Cancel invitation error:', error);
      throw error;
    }
  },

  /**
   * Delete an invitation
   */
  async deleteInvitation(organizationId: string, invitationId: string): Promise<void> {
    try {
      // Get the invitation to find the token
      const invitationRef = doc(db, 'organizations', organizationId, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);

      if (invitationDoc.exists()) {
        const invitation = invitationDoc.data() as Invitation;

        // Delete the token lookup document if it exists
        if (invitation.token) {
          await deleteDoc(doc(db, 'invitationTokens', invitation.token));
        }
      }

      await deleteDoc(invitationRef);
    } catch (error) {
      console.error('Delete invitation error:', error);
      throw error;
    }
  },

  /**
   * Resend an invitation
   * Updates the expiration date and generates a new token
   */
  async resendInvitation(
    organizationId: string,
    invitationId: string,
    options: {
      organizationName: string;
      employeeName?: string;
    }
  ): Promise<{ token: string; invitationLink: string }> {
    try {
      const invitationRef = doc(db, 'organizations', organizationId, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);

      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }

      const invitation = invitationDoc.data() as Invitation;

      // Delete old token lookup document
      if (invitation.token) {
        await deleteDoc(doc(db, 'invitationTokens', invitation.token));
      }

      // Generate new token and expiration
      const newToken = generateInvitationToken();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      await updateDoc(invitationRef, {
        token: newToken,
        expiresAt: Timestamp.fromDate(expiresAt),
        status: 'pending',
      });

      // Create new token lookup document with public data
      await this.createInvitationTokenDocument(newToken, organizationId, invitationId, {
        email: invitation.email,
        role: invitation.role,
        invitationType: invitation.invitationType || 'staff',
        organizationName: options.organizationName,
        employeeName: options.employeeName,
        employeeId: invitation.employeeId,
        expiresAt,
      });

      const invitationLink = `${getInvitationBaseUrl()}/invite/${newToken}`;

      return { token: newToken, invitationLink };
    } catch (error) {
      console.error('Resend invitation error:', error);
      throw error;
    }
  },

  /**
   * Clean up expired invitations
   * Should be called periodically or via Cloud Functions
   */
  async cleanupExpiredInvitations(organizationId: string): Promise<number> {
    try {
      const invitationsRef = collection(db, 'organizations', organizationId, 'invitations');
      const q = query(invitationsRef, where('status', '==', 'pending'));
      const querySnapshot = await getDocs(q);

      const now = new Date();
      let expiredCount = 0;

      const updatePromises = querySnapshot.docs.map(async (docSnapshot) => {
        const invitation = docSnapshot.data() as Invitation;
        const expiresAt = invitation.expiresAt.toDate();

        if (now > expiresAt) {
          // Delete token lookup document
          if (invitation.token) {
            await deleteDoc(doc(db, 'invitationTokens', invitation.token));
          }

          await updateDoc(docSnapshot.ref, {
            status: 'expired',
          });
          expiredCount++;
        }
      });

      await Promise.all(updatePromises);
      return expiredCount;
    } catch (error) {
      console.error('Cleanup expired invitations error:', error);
      throw error;
    }
  },
};
